import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useSearchParams } from "react-router-dom";
import { format, addDays, subDays } from "date-fns";
import { tr } from "date-fns/locale";
import axios from "axios";
import { ChevronLeft, ChevronRight, RefreshCw, AlertTriangle, Check, X, Download, MoveRight, Plus } from "lucide-react";
import ManualEntryDialog from "./ManualEntryDialog";

const API = window.location.origin;

// Column config. `field` = PATCH field when different from display key.
const COLUMNS = [
  { key: "row_num", label: "#", width: 40, frozen: true },
  { key: "booking_time", label: "Saat", width: 55, frozen: true },
  { key: "customer_name", label: "Musteri", width: 150, frozen: true },
  { key: "confirmation_code", label: "Kod", width: 105 },
  { key: "total_pax", label: "Pax", width: 45 },
  { key: "product_name", label: "Urun", width: 190 },
  { key: "badges", label: "Etiketler", width: 200 },
  { key: "hotel_name", label: "Otel", width: 130 },
  { key: "customer_phone", label: "Telefon", width: 120 },
  { key: "kalkis", label: "Kalkis", width: 85, editable: true, type: "text" },
  { key: "masa_no", label: "Masa", width: 70, editable: true, type: "text" },
  { key: "garson", label: "Garson", width: 100, editable: true, type: "text" },
  { key: "status", label: "Durum", width: 95, editable: true, type: "status" },
  { key: "tahsilat_sekli", label: "Tahsilat", width: 90, editable: true, type: "payment" },
  { key: "tutar", label: "Tutar", width: 75, editable: true, type: "number" },
  { key: "op_notes", label: "Not", width: 160, editable: true, type: "text", field: "notes" },
  { key: "boat_name", label: "Tekne", width: 110, editable: true, type: "boat", field: "boat_id" },
];

// Canonical status values (English, stored in DB) with Turkish display labels
const STATUS_OPTS = [
  { v: "pending", l: "Bekliyor", cls: "bg-gray-100 text-gray-600" },
  { v: "checked-in", l: "Geldi", cls: "bg-green-100 text-green-700" },
  { v: "no_show", l: "Gelmedi", cls: "bg-amber-100 text-amber-700" },
  { v: "cancelled", l: "Iptal", cls: "bg-red-100 text-red-600" },
];
const STATUS_MAP = { bekliyor: "pending", geldi: "checked-in", gelmedi: "no_show", iptal: "cancelled", check_in: "checked-in", "no-show": "no_show" };
const normStatus = (s) => STATUS_MAP[s] || s || "pending";

const PAYMENT_OPTS = [
  { v: "", l: "-" }, { v: "nakit", l: "Nakit" }, { v: "kart", l: "Kart" },
  { v: "online", l: "Online" }, { v: "acenta", l: "Acenta" },
];

// ---- Opsiyon rozetleri (Operasyon Sayfasi ile ayni) ----
function parseOptionBadges(optionName, productName) {
  const badges = [];
  const text = ((optionName || "") + " " + (productName || "")).toLowerCase();
  if (text.includes("transferli") || text.includes("with transfer") || text.includes("hotel transfer") || text.includes("and transfer") || text.includes("alcohole transfer")) {
    badges.push({ label: "Transferli", bg: "#2563EB" });
  } else if (text.includes("kendi gelir") || text.includes("kendigelir") || text.includes("no transfer") || text.includes("meeting point")) {
    badges.push({ label: "Kendi Gelir", bg: "#6B7280" });
  }
  if (text.includes("vip")) badges.push({ label: "VIP", bg: "#7C3AED" });
  if (text.includes("limitsiz") || text.includes("unlimited")) badges.push({ label: "Limitsiz", bg: "#DC2626" });
  if (text.includes("alkollü") || text.includes("alkollu") || text.includes("alcoholic") || text.includes("alcohole") || text.includes("limited alcohol")) {
    badges.push({ label: "Alkollu", bg: "#EA580C" });
  } else if (text.includes("alkolsüz") || text.includes("alkolsuz") || text.includes("non-alcohol") || text.includes("soft drink")) {
    badges.push({ label: "Alkolsuz", bg: "#16A34A" });
  }
  if (text.includes("romantic")) badges.push({ label: "Romantic", bg: "#DB2777" });
  if (text.includes("snack")) badges.push({ label: "Snack", bg: "#0891B2" });
  if (text.includes("private table") || text.includes("özel masa") || text.includes("ozel masa")) badges.push({ label: "PVT", bg: "#0E7490" });
  if (text.includes("window") || text.includes("cam kenar")) badges.push({ label: "Cam Kenari", bg: "#B45309" });
  return badges;
}

function getAllBadges(optionName, productName, customTags) {
  const badges = parseOptionBadges(optionName, productName);
  if (!customTags || customTags.length === 0) return badges;
  const text = ((optionName || "") + " " + (productName || "")).toLowerCase();
  for (const ct of customTags) {
    const pattern = (ct.match_pattern || "").toLowerCase();
    if (!pattern) continue;
    let searchIn = text;
    if (ct.match_field === "option_name") searchIn = (optionName || "").toLowerCase();
    else if (ct.match_field === "product_name") searchIn = (productName || "").toLowerCase();
    if (searchIn.includes(pattern) && !badges.find(b => b.label === ct.tag_label)) {
      badges.push({ label: ct.tag_label, bg: ct.tag_color, custom: true });
    }
  }
  return badges;
}

const ROW_HEIGHT = 36;
const HEADER_HEIGHT = 40;
const FROZEN_COUNT = 3;
const CHECKBOX_W = 36;
const TOTAL_WIDTH = COLUMNS.reduce((s, c) => s + c.width, 0);
const GRID_WIDTH = CHECKBOX_W + TOTAL_WIDTH;
const ATANMAMIS_NAME = "Atanmamis";

const normTime = (t) => {
  if (!t) return "saatsiz";
  const s = String(t).slice(0, 5);
  return s || "saatsiz";
};

function displayValue(row, col, boatMap) {
  if (col.key === "boat_name") return boatMap[row.boat_id] || "-";
  if (col.key === "status") {
    const s = normStatus(row.status);
    const opt = STATUS_OPTS.find(o => o.v === s);
    return opt ? opt.l : s;
  }
  if (col.key === "tutar") return row.tutar != null ? row.tutar : "";
  const v = row[col.key];
  return v == null || v === "" ? "" : v;
}

// ---- Editable cell input ----
function CellEditor({ col, initialValue, boats, onCommit, onCancel }) {
  const ref = useRef(null);
  const [val, setVal] = useState(initialValue ?? "");

  useEffect(() => {
    if (ref.current) { ref.current.focus(); if (ref.current.select) ref.current.select(); }
  }, []);

  const commit = () => onCommit(val);
  const handleKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); commit(); }
    else if (e.key === "Escape") { e.preventDefault(); onCancel(); }
    else if (e.key === "Tab") { e.preventDefault(); commit(); }
  };
  const baseCls = "w-full h-full px-1 text-xs outline-none border-2 border-blue-500 bg-white";

  if (col.type === "status") {
    return (
      <select ref={ref} value={normStatus(val)} data-testid="cell-editor-select"
        onChange={(e) => onCommit(e.target.value)} onKeyDown={handleKey} onBlur={onCancel} className={baseCls}>
        {STATUS_OPTS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    );
  }
  if (col.type === "payment") {
    return (
      <select ref={ref} value={val || ""} data-testid="cell-editor-select"
        onChange={(e) => onCommit(e.target.value)} onKeyDown={handleKey} onBlur={onCancel} className={baseCls}>
        {PAYMENT_OPTS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    );
  }
  if (col.type === "boat") {
    return (
      <select ref={ref} value={val ?? ""} data-testid="cell-editor-select"
        onChange={(e) => onCommit(e.target.value === "" ? null : parseInt(e.target.value))}
        onKeyDown={handleKey} onBlur={onCancel} className={baseCls}>
        <option value="">-</option>
        {boats.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
      </select>
    );
  }
  return (
    <input ref={ref} type={col.type === "number" ? "number" : "text"} value={val} data-testid="cell-editor-input"
      onChange={(e) => setVal(e.target.value)} onKeyDown={handleKey} onBlur={commit} className={baseCls} />
  );
}

// ---- Display cell ----
const Cell = React.memo(function Cell({ row, col, colIdx, ri, boatMap, isSelected, isEditing, isConflict, boats, customTags, onSelect, onStartEdit, onCommit, onCancelEdit }) {
  const editable = !!col.editable;
  if (col.key === "badges") {
    const badges = getAllBadges(row.option_name, row.product_name, customTags);
    return (
      <div data-testid={`cell-${ri}-badges`} onClick={() => onSelect()}
        style={{ width: col.width, minWidth: col.width, height: ROW_HEIGHT }}
        className="flex items-center gap-1 border-r border-gray-100 shrink-0 px-1.5 overflow-x-auto hover:bg-gray-50">
        {badges.length === 0 ? <span className="text-gray-300 text-xs">-</span> : badges.map((b, i) => (
          <span key={i} title={b.label} style={{ backgroundColor: b.bg }}
            className="shrink-0 text-[9px] font-bold leading-none px-1.5 py-0.5 rounded text-white">{b.label}</span>
        ))}
      </div>
    );
  }
  const shown = displayValue(row, col, boatMap);
  return (
    <div
      data-testid={`cell-${ri}-${col.key}`}
      onClick={() => onSelect()}
      onDoubleClick={() => editable && onStartEdit()}
      style={{ width: col.width, minWidth: col.width, height: ROW_HEIGHT }}
      className={`relative flex items-center border-r border-gray-100 shrink-0 text-xs
        ${editable ? "cursor-cell" : "cursor-default"}
        ${isConflict ? "outline outline-2 outline-red-500 -outline-offset-2 bg-red-50 z-20" :
          isSelected ? "outline outline-2 outline-blue-500 -outline-offset-2 bg-blue-50 z-20" : "hover:bg-gray-50"}
        ${col.key === "row_num" ? "justify-center text-[10px] font-mono text-gray-400 bg-gray-50/50" : "text-gray-800"}`}>
      {isEditing ? (
        <CellEditor col={col} boats={boats}
          initialValue={col.key === "boat_name" ? row.boat_id : (col.key === "status" ? normStatus(row.status) : row[col.key])}
          onCommit={onCommit} onCancel={onCancelEdit} />
      ) : col.key === "status" ? (
        <span className={`ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${STATUS_OPTS.find(o => o.v === normStatus(row.status))?.cls || "bg-gray-100 text-gray-600"}`}>{shown}</span>
      ) : col.key === "total_pax" ? (
        <b className="px-2">{shown || 0}</b>
      ) : col.key === "customer_name" ? (
        <span className="px-2 font-medium truncate flex items-center gap-1 w-full">
          <span className="truncate">{shown || "-"}</span>
          {row.source === "manual" && (
            <span data-testid="manual-badge" title="Manuel kayit"
              className="shrink-0 text-[8px] font-bold leading-none px-1 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300">MANUEL</span>
          )}
        </span>
      ) : col.key === "tutar" ? (
        <span className="px-2 truncate">{shown !== "" ? `${shown} \u20BA` : ""}</span>
      ) : (
        <span className={`px-2 truncate ${editable && shown === "" ? "text-gray-300" : ""}`}>{shown !== "" ? shown : (editable ? "\u2014" : "-")}</span>
      )}
    </div>
  );
});

export default function LiveGrid() {
  const [date, setDate] = useState(new Date());
  const [rows, setRows] = useState([]);
  const [boats, setBoats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCell, setSelectedCell] = useState(null); // {rowIdx, colIdx} into visibleRows
  const [editingCell, setEditingCell] = useState(null);
  const [conflict, setConflict] = useState(null); // {rowId, colIdx, field, current_value, current_version, updated_by, pendingValue}
  const [status, setStatus] = useState({ type: "idle", msg: "" });
  const [wsConnected, setWsConnected] = useState(false);
  const [presenceUsers, setPresenceUsers] = useState([]);
  const [timeFilters, setTimeFilters] = useState(new Set());
  const [selectedRowIds, setSelectedRowIds] = useState(new Set());
  const [manualOpen, setManualOpen] = useState(false);
  const [pasteData, setPasteData] = useState(null);   // {changes:[], warnings:[]}
  const [pasteResult, setPasteResult] = useState(null); // {ok, conflict, err}
  const [lastPaste, setLastPaste] = useState(null); // [{rowId, field, oldValue}] — geri al icin
  const [categories, setCategories] = useState([]);
  const [customTags, setCustomTags] = useState([]);
  const [unmappedCount, setUnmappedCount] = useState(0);
  const [dragRowId, setDragRowId] = useState(null);
  const [mapChoice, setMapChoice] = useState({});

  const [searchParams, setSearchParams] = useSearchParams();
  const scrollRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectRef = useRef({ attempts: 0, timer: null, closed: false });
  const pendingRefetchRef = useRef(false);
  const editingCellRef = useRef(null);
  const token = localStorage.getItem("auth_token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const dateStr = format(date, "yyyy-MM-dd");
  const dateDisplay = format(date, "d MMMM yyyy, EEEE", { locale: tr });

  const boatMap = useMemo(() => {
    const m = {}; boats.forEach(b => { m[b.id] = b.name; }); return m;
  }, [boats]);
  const atanmamisId = useMemo(() => boats.find(b => b.name === ATANMAMIS_NAME)?.id, [boats]);

  // ---- boat filter from URL (?boat=all|<id>), default = ATANMAMIS ----
  const boatParam = searchParams.get("boat");
  const boatFilter = useMemo(() => {
    if (boatParam === "all") return "all";
    if (boatParam && !isNaN(Number(boatParam))) return Number(boatParam);
    return atanmamisId ?? "all";
  }, [boatParam, atanmamisId]);

  const setBoatFilter = (val) => {
    const p = new URLSearchParams(searchParams);
    p.set("boat", String(val));
    setSearchParams(p, { replace: true });
  };

  // ---- kategori filtresi (?cat=all|<id>|unmapped), default = all ----
  const catParam = searchParams.get("cat");
  const activeCategory = catParam == null || catParam === "all" ? "all"
    : catParam === "unmapped" ? "unmapped" : Number(catParam);
  const setActiveCategory = (val) => {
    const p = new URLSearchParams(searchParams);
    p.set("cat", String(val));
    setSearchParams(p, { replace: true });
  };

  const fetchSheet = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/sheet/${dateStr}`, { headers });
      setBoats(data.boats || []);
      setCategories(data.categories || []);
      setUnmappedCount(data.unmapped_count || 0);
      setRows((data.rows || []).map((r, i) => ({ ...r, __idx: i, row_num: i + 1 })));
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [dateStr]);

  useEffect(() => { fetchSheet(); }, [fetchSheet]);

  // Ozel etiketleri bir kez getir (rozetler icin)
  useEffect(() => {
    axios.get(`${API}/api/custom-tags`, { headers })
      .then(res => setCustomTags((res.data?.tags || []).filter(t => t.active)))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Silent refetch (spinner YOK) — scroll/selection korunur cunku grid unmount olmaz
  const fetchSheetSilent = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/sheet/${dateStr}`, { headers });
      setBoats(data.boats || []);
      setCategories(data.categories || []);
      setUnmappedCount(data.unmapped_count || 0);
      setRows((data.rows || []).map((r, i) => ({ ...r, __idx: i, row_num: i + 1 })));
    } catch (e) { console.error(e); }
  }, [dateStr]);

  // row_new refetch'i, kullanici hucre duzenlerken ERTELE (yazdigini silmesin);
  // duzenleme bitince uygula.
  useEffect(() => {
    if (!editingCell && pendingRefetchRef.current) {
      pendingRefetchRef.current = false;
      fetchSheetSilent();
    }
  }, [editingCell, fetchSheetSilent]);

  useEffect(() => { editingCellRef.current = editingCell; }, [editingCell]);

  // ---- FAZ B: apply a remote delta coming from WebSocket ----
  const applyRemote = useCallback((rowId, fields, version, updatedBy) => {
    setRows(prev => prev.map(r => {
      if (r.id !== rowId) return r;
      if (version != null && (r.version || 1) >= version) return r;
      const nr = { ...r, version: version ?? r.version, updated_by: updatedBy ?? r.updated_by };
      Object.entries(fields || {}).forEach(([k, v]) => {
        if (k === "notes") nr.op_notes = v;
        else if (k === "manual_edits") { Object.entries(v || {}).forEach(([mk, mv]) => { nr[mk] = mv; }); }
        else nr[k] = v;
      });
      return nr;
    }));
  }, []);

  // ---- FAZ B: WebSocket live-sync (date-scoped room, auto-reconnect) ----
  useEffect(() => {
    if (!token) return;
    const rc = reconnectRef.current;
    rc.closed = false;
    const connect = () => {
      const wsUrl = `${API.replace(/^http/, "ws")}/api/ws/sheet?date=${dateStr}&token=${encodeURIComponent(token)}`;
      let ws;
      try { ws = new WebSocket(wsUrl); } catch { return; }
      wsRef.current = ws;
      ws.onopen = () => {
        setWsConnected(true);
        if (rc.attempts > 0) fetchSheet();
        rc.attempts = 0;
      };
      ws.onmessage = (ev) => {
        let msg; try { msg = JSON.parse(ev.data); } catch { return; }
        if (msg.type === "cell_update" || msg.type === "row_update") {
          applyRemote(msg.row_id, msg.fields, msg.version, msg.updated_by);
        } else if (msg.type === "row_new") {
          // Yeni manuel kayit(lar): duzenleme aktifse ertele, degilse sessiz refetch
          if (editingCellRef.current) pendingRefetchRef.current = true;
          else fetchSheetSilent();
        } else if (msg.type === "presence" && Array.isArray(msg.users)) {
          setPresenceUsers(msg.users);
        }
      };
      ws.onclose = () => {
        setWsConnected(false);
        if (rc.closed) return;
        rc.attempts = Math.min(rc.attempts + 1, 6);
        const delay = Math.min(1000 * 2 ** (rc.attempts - 1), 15000);
        rc.timer = setTimeout(connect, delay);
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };
    connect();
    return () => {
      rc.closed = true;
      if (rc.timer) clearTimeout(rc.timer);
      try { wsRef.current && wsRef.current.close(); } catch {}
      setWsConnected(false);
    };
  }, [dateStr, token, applyRemote, fetchSheet, fetchSheetSilent]);

  // ---- B2.1/B2.3: derived tab + time-chip + visible rows ----
  const catRows = useMemo(() => {
    if (activeCategory === "all") return rows;
    if (activeCategory === "unmapped") return rows.filter(r => r.category_id == null);
    return rows.filter(r => r.category_id === activeCategory);
  }, [rows, activeCategory]);

  const catCounts = useMemo(() => {
    const m = {}; let unm = 0;
    rows.forEach(r => { if (r.category_id == null) unm++; else m[r.category_id] = (m[r.category_id] || 0) + 1; });
    return { map: m, unmapped: unm };
  }, [rows]);

  const unmappedProducts = useMemo(() => {
    if (activeCategory !== "unmapped") return [];
    const m = {};
    rows.filter(r => r.category_id == null).forEach(r => {
      const pn = (r.product_name || "").trim();
      m[pn] = (m[pn] || 0) + 1;
    });
    return Object.entries(m).map(([product_name, count]) => ({ product_name, count })).sort((a, b) => b.count - a.count);
  }, [rows, activeCategory]);

  const paxByBoat = useMemo(() => {
    const m = {}; catRows.forEach(r => { m[r.boat_id] = (m[r.boat_id] || 0) + (r.total_pax || 0); }); return m;
  }, [catRows]);
  const totalPax = useMemo(() => catRows.reduce((s, r) => s + (r.total_pax || 0), 0), [catRows]);

  const tabs = useMemo(() => {
    const t = [{ id: "all", label: "TUMU", pax: totalPax, cap: null }];
    const atan = boats.find(b => b.name === ATANMAMIS_NAME);
    if (atan) t.push({ id: atan.id, label: "ATANMAMIS", pax: paxByBoat[atan.id] || 0, cap: atan.capacity });
    boats.filter(b => b.name !== ATANMAMIS_NAME)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .forEach(b => t.push({ id: b.id, label: b.name, pax: paxByBoat[b.id] || 0, cap: b.capacity }));
    return t;
  }, [boats, paxByBoat, totalPax]);

  const boatRows = useMemo(() => (
    boatFilter === "all" ? catRows : catRows.filter(r => r.boat_id === boatFilter)
  ), [catRows, boatFilter]);

  const timeChips = useMemo(() => {
    const m = {};
    boatRows.forEach(r => { const t = normTime(r.booking_time); m[t] = (m[t] || 0) + (r.total_pax || 0); });
    return Object.entries(m).sort((a, b) => {
      if (a[0] === "saatsiz") return 1;
      if (b[0] === "saatsiz") return -1;
      return a[0].localeCompare(b[0]);
    }).map(([t, pax]) => ({ t, pax }));
  }, [boatRows]);

  const visibleRows = useMemo(() => (
    timeFilters.size === 0 ? boatRows : boatRows.filter(r => timeFilters.has(normTime(r.booking_time)))
  ), [boatRows, timeFilters]);

  // Clear selection/editing when the visible set changes
  useEffect(() => { setSelectedCell(null); setEditingCell(null); }, [boatFilter, timeFilters, dateStr, activeCategory]);

  const rowVirtualizer = useVirtualizer({
    count: visibleRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
  });

  // ---- Save a single cell via versioned PATCH endpoint (id-based) ----
  const saveCell = useCallback(async (rowIdx, colIdx, rawValue, forceVersion = null) => {
    const col = COLUMNS[colIdx];
    const row = visibleRows[rowIdx];
    if (!row || !col.editable || !row.id) return;
    const rowId = row.id;
    const field = col.field || col.key;
    const key = col.key === "boat_name" ? "boat_id" : col.key;

    let value = rawValue;
    if (col.type === "number") value = rawValue === "" || rawValue == null ? null : parseFloat(rawValue);
    else if (col.type === "boat") value = rawValue === "" || rawValue == null ? null : parseInt(rawValue);
    else if (typeof rawValue === "string") value = rawValue;

    const expected_version = forceVersion != null ? forceVersion : (row.version || 1);
    const prevRow = { ...row };
    const patchById = (mut) => setRows(prev => prev.map(r => r.id === rowId ? mut(r) : r));
    patchById(r => ({ ...r, [key]: value }));
    setStatus({ type: "saving", msg: "Kaydediliyor..." });

    try {
      const resp = await axios.patch(`${API}/api/sheet/row/${rowId}/cell`,
        { field, value, expected_version }, { headers, validateStatus: () => true });
      if (resp.status === 200 && resp.data.ok) {
        patchById(r => ({ ...r, version: resp.data.version, updated_by: resp.data.updated_by }));
        setConflict(null);
        setStatus({ type: "saved", msg: `Kaydedildi (v${resp.data.version})` });
        setTimeout(() => setStatus(s => s.type === "saved" ? { type: "idle", msg: "" } : s), 2000);
      } else if (resp.status === 409 && resp.data.conflict) {
        patchById(() => prevRow);
        setConflict({
          rowId, colIdx, field,
          current_value: resp.data.current_value,
          current_version: resp.data.current_version,
          updated_by: resp.data.updated_by,
          pendingValue: value,
        });
        setStatus({ type: "error", msg: "Cakisma tespit edildi" });
      } else {
        patchById(() => prevRow);
        setStatus({ type: "error", msg: resp.data?.detail || "Kayit hatasi" });
      }
    } catch (e) {
      patchById(() => prevRow);
      setStatus({ type: "error", msg: "Baglanti hatasi" });
    }
  }, [visibleRows]);

  // ---- Conflict resolution (id-based) ----
  const conflictKey = (field) => field === "notes" ? "op_notes" : field;
  const resolveRefresh = useCallback(() => {
    if (!conflict) return;
    setRows(prev => prev.map(r => r.id === conflict.rowId
      ? { ...r, [conflictKey(conflict.field)]: conflict.current_value, version: conflict.current_version }
      : r));
    setConflict(null);
    setStatus({ type: "idle", msg: "" });
  }, [conflict]);

  const resolveOverwrite = useCallback(() => {
    if (!conflict) return;
    const idx = visibleRows.findIndex(r => r.id === conflict.rowId);
    if (idx < 0) { setConflict(null); return; }
    setRows(prev => prev.map(r => r.id === conflict.rowId ? { ...r, version: conflict.current_version } : r));
    saveCell(idx, conflict.colIdx, conflict.pendingValue, conflict.current_version);
  }, [conflict, saveCell, visibleRows]);

  // ---- Cell selection / editing ----
  const startEdit = useCallback((rowIdx, colIdx) => {
    if (COLUMNS[colIdx]?.editable) setEditingCell({ rowIdx, colIdx });
  }, []);
  const commitEdit = useCallback((rowIdx, colIdx, value) => {
    setEditingCell(null);
    saveCell(rowIdx, colIdx, value);
  }, [saveCell]);

  const handleKeyDown = useCallback((e) => {
    if (editingCell) return;
    if (!selectedCell) return;
    let { rowIdx, colIdx } = selectedCell;
    const maxRow = visibleRows.length - 1;
    switch (e.key) {
      case "ArrowDown": e.preventDefault(); rowIdx = Math.min(maxRow, rowIdx + 1); break;
      case "ArrowUp": e.preventDefault(); rowIdx = Math.max(0, rowIdx - 1); break;
      case "ArrowRight": e.preventDefault(); colIdx = Math.min(COLUMNS.length - 1, colIdx + 1); break;
      case "ArrowLeft": e.preventDefault(); colIdx = Math.max(0, colIdx - 1); break;
      case "Tab": e.preventDefault(); colIdx = e.shiftKey ? Math.max(0, colIdx - 1) : Math.min(COLUMNS.length - 1, colIdx + 1); break;
      case "Enter": case "F2":
        e.preventDefault();
        if (COLUMNS[colIdx].editable) { startEdit(rowIdx, colIdx); return; }
        rowIdx = Math.min(maxRow, rowIdx + 1); break;
      case "Escape": setSelectedCell(null); return;
      default:
        if (COLUMNS[colIdx].editable && e.key.length === 1 && !e.metaKey && !e.ctrlKey) startEdit(rowIdx, colIdx);
        return;
    }
    setSelectedCell({ rowIdx, colIdx });
    rowVirtualizer.scrollToIndex(rowIdx, { align: "auto" });
  }, [selectedCell, editingCell, visibleRows.length, rowVirtualizer, startEdit]);

  const goDate = (d) => { setSelectedCell(null); setEditingCell(null); setConflict(null); setSelectedRowIds(new Set()); setDate(d); };

  // ---- D4: Blok yapistirma (TSV -> onizleme -> batch). Var olan satirlari GUNCELLER. ----
  const buildPastePreview = useCallback((text) => {
    if (!selectedCell) return;
    const grid = text.replace(/\r/g, "").split("\n");
    if (grid.length && grid[grid.length - 1] === "") grid.pop();
    const matrix = grid.map(l => l.split("\t"));
    const { rowIdx, colIdx } = selectedCell;
    const changes = []; const warnings = [];
    let skippedRO = 0, overflow = 0;
    matrix.forEach((cells, r) => {
      const tRow = rowIdx + r;
      if (tRow >= visibleRows.length) { overflow += cells.length; return; }
      const row = visibleRows[tRow];
      cells.forEach((val, c) => {
        const tCol = colIdx + c;
        if (tCol >= COLUMNS.length) { overflow++; return; }
        const col = COLUMNS[tCol];
        if (!col.editable || col.type === "boat") { skippedRO++; return; }
        const field = col.field || col.key;
        const oldVal = col.key === "op_notes" ? row.op_notes : (col.key === "status" ? row.status : row[col.key]);
        changes.push({ rowId: row.id, colKey: col.key, colLabel: col.label, field,
          oldValue: oldVal == null ? "" : String(oldVal), newValue: val,
          expected_version: row.version || 1, customer: row.customer_name });
      });
    });
    if (skippedRO) warnings.push(`${skippedRO} hucre salt-okunur/tekne sutununa denk geldi, atlandi`);
    if (overflow) warnings.push(`${overflow} hucre defterin disina tasti, atildi`);
    if (changes.length > 100) warnings.push(`100 hucre siniri: ilk 100 uygulanacak`);
    setPasteResult(null);
    setPasteData({ changes: changes.slice(0, 100), warnings });
  }, [selectedCell, visibleRows]);

  const applyPaste = async () => {
    if (!pasteData || !pasteData.changes.length) return;
    const cells = pasteData.changes.map(ch => ({
      row_id: ch.rowId, field: ch.field,
      value: ch.field === "tutar" ? (ch.newValue === "" ? null : parseFloat(ch.newValue)) : ch.newValue,
      expected_version: ch.expected_version,
    }));
    setStatus({ type: "saving", msg: "Yapistiriliyor..." });
    try {
      const { data } = await axios.patch(`${API}/api/sheet/cells/batch`, { cells }, { headers });
      const results = data.results || [];
      let ok = 0, conflict = 0, err = 0;
      results.forEach(res => { if (res.status === "ok") ok++; else if (res.status === "conflict") conflict++; else err++; });
      // Geri al icin eski degerleri sakla
      setLastPaste(pasteData.changes.map(ch => ({ rowId: ch.rowId, field: ch.field, oldValue: ch.oldValue })));
      setPasteData(null);
      setPasteResult({ ok, conflict, err });
      setStatus({ type: conflict || err ? "error" : "saved", msg: `${ok} guncellendi${conflict ? `, ${conflict} cakisti` : ""}${err ? `, ${err} hata` : ""}` });
      fetchSheetSilent();
      setTimeout(() => setStatus(s => s.type === "saved" ? { type: "idle", msg: "" } : s), 3000);
    } catch (e) {
      setStatus({ type: "error", msg: "Yapistirma hatasi" });
      setPasteData(null);
    }
  };

  useEffect(() => {
    const onPaste = (e) => {
      if (!selectedCell || editingCell || manualOpen) return;
      const text = e.clipboardData && e.clipboardData.getData("text");
      if (!text) return;
      e.preventDefault();
      buildPastePreview(text);
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [selectedCell, editingCell, manualOpen, buildPastePreview]);


  // ---- B2.2: selection + bulk move ----
  const toggleRow = (id) => setSelectedRowIds(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const allVisibleSelected = visibleRows.length > 0 && visibleRows.every(r => selectedRowIds.has(r.id));
  const toggleSelectAll = () => setSelectedRowIds(prev => {
    if (allVisibleSelected) { const n = new Set(prev); visibleRows.forEach(r => n.delete(r.id)); return n; }
    const n = new Set(prev); visibleRows.forEach(r => n.add(r.id)); return n;
  });
  const selectedCount = selectedRowIds.size;
  const selectedPax = useMemo(() => rows.filter(r => selectedRowIds.has(r.id)).reduce((s, r) => s + (r.total_pax || 0), 0), [rows, selectedRowIds]);

  const doBulkMove = async (targetBoatId) => {
    const ids = [...selectedRowIds];
    if (!ids.length || !targetBoatId) return;
    const tid = Number(targetBoatId);
    setRows(prev => prev.map(r => selectedRowIds.has(r.id) ? { ...r, boat_id: tid } : r));
    setStatus({ type: "saving", msg: `${ids.length} rez. tasiniyor...` });
    try {
      const { data } = await axios.post(`${API}/api/sheet/bulk-move`, { row_ids: ids, target_boat_id: tid }, { headers });
      setSelectedRowIds(new Set());
      const failMsg = data.failed && data.failed.length ? `, ${data.failed.length} basarisiz` : "";
      setStatus({ type: "saved", msg: `${data.moved} tasindi${failMsg}` });
      setTimeout(() => setStatus(s => s.type === "saved" ? { type: "idle", msg: "" } : s), 2500);
    } catch (e) {
      setStatus({ type: "error", msg: "Toplu tasima hatasi" });
      fetchSheet();
    }
  };

  // Surukle-birak / tek satir tekne atamasi
  const assignBoat = async (rowId, targetBoatId) => {
    if (!rowId || targetBoatId == null) return;
    const tid = Number(targetBoatId);
    const cur = rows.find(r => r.id === rowId);
    if (cur && cur.boat_id === tid) return;
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, boat_id: tid } : r));
    setStatus({ type: "saving", msg: "Tekneye atanıyor..." });
    try {
      await axios.post(`${API}/api/sheet/bulk-move`, { row_ids: [rowId], target_boat_id: tid }, { headers });
      setStatus({ type: "saved", msg: "Tekneye atandı" });
      setTimeout(() => setStatus(s => s.type === "saved" ? { type: "idle", msg: "" } : s), 2000);
    } catch (e) {
      setStatus({ type: "error", msg: "Atama hatası" });
      fetchSheet();
    }
  };

  // Eslenmemis urunu kategoriye bagla
  const mapProduct = async (productName, categoryId) => {
    if (!categoryId) return;
    try {
      await axios.post(`${API}/api/category-mappings`, { product_name: productName, category_id: Number(categoryId) }, { headers });
      setStatus({ type: "saved", msg: "Ürün kategoriye eşlendi" });
      setTimeout(() => setStatus(s => s.type === "saved" ? { type: "idle", msg: "" } : s), 2000);
      fetchSheet();
    } catch (e) {
      setStatus({ type: "error", msg: "Eşleme hatası" });
    }
  };

  const undoPaste = async () => {
    if (!lastPaste || !lastPaste.length) return;
    const cells = lastPaste.map(ch => {
      const row = rows.find(r => r.id === ch.rowId);
      return {
        row_id: ch.rowId, field: ch.field,
        value: ch.field === "tutar" ? (ch.oldValue === "" ? null : parseFloat(ch.oldValue)) : ch.oldValue,
        expected_version: row ? (row.version || 1) : 1,
      };
    });
    setStatus({ type: "saving", msg: "Geri aliniyor..." });
    try {
      const { data } = await axios.patch(`${API}/api/sheet/cells/batch`, { cells }, { headers });
      const rr = data.results || [];
      const okc = rr.filter(r => r.status === "ok").length;
      setLastPaste(null);
      setStatus({ type: "saved", msg: `Yapistirma geri alindi (${okc} hucre)` });
      fetchSheetSilent();
      setTimeout(() => setStatus(s => s.type === "saved" ? { type: "idle", msg: "" } : s), 2500);
    } catch (e) {
      setStatus({ type: "error", msg: "Geri alma hatasi" });
    }
  };

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalHeight = rowVirtualizer.getTotalSize();

  const tabColor = (pax, cap) => {
    if (!cap || cap <= 0) return "";
    if (pax > cap) return "ring-1 ring-red-400 text-red-700";
    if (pax >= cap * 0.9) return "ring-1 ring-amber-400 text-amber-700";
    return "";
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col" data-testid="live-grid" onKeyDown={handleKeyDown} tabIndex={0}>
      {/* Header / date nav */}
      <div className="bg-white border-b px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <h1 className="text-base font-bold text-gray-800">Canli Defter</h1>
          <span className="text-[10px] font-semibold uppercase tracking-wide bg-blue-100 text-blue-700 px-2 py-0.5 rounded">BETA</span>
          <span data-testid="ws-status" className={`flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded ${wsConnected ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-500"}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${wsConnected ? "bg-green-500 animate-pulse" : "bg-gray-400"}`} />
            {wsConnected ? "Canli" : "Baglaniyor..."}
          </span>
          {presenceUsers.length > 1 && (
            <span data-testid="presence-count" className="text-[10px] text-gray-500">{presenceUsers.length} kisi cevrimici</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button data-testid="date-prev" onClick={() => goDate(subDays(date, 1))} className="p-1.5 rounded hover:bg-gray-100 text-gray-600"><ChevronLeft className="w-4 h-4" /></button>
          <div className="text-sm font-semibold text-gray-700 min-w-[190px] text-center" data-testid="date-display">{dateDisplay}</div>
          <button data-testid="date-next" onClick={() => goDate(addDays(date, 1))} className="p-1.5 rounded hover:bg-gray-100 text-gray-600"><ChevronRight className="w-4 h-4" /></button>
          <button data-testid="date-today" onClick={() => goDate(new Date())} className="ml-1 text-xs font-medium px-2.5 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-700">Bugun</button>
          <button data-testid="grid-refresh" onClick={fetchSheet} className="p-1.5 rounded hover:bg-gray-100 text-gray-600"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></button>
          <button data-testid="manual-entry-btn" onClick={() => setManualOpen(true)}
            className="ml-1 text-xs font-semibold px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Manuel giris
          </button>
        </div>
        <div className="text-xs text-gray-500" data-testid="row-count">{visibleRows.length} / {rows.length} rez.</div>
      </div>

      {/* Kategori sekmeleri (Operasyon Sayfasi gibi) */}
      <div className="bg-white border-b px-3 py-2 flex items-center gap-2 overflow-x-auto" data-testid="lg-category-tabs">
        <button data-testid="lg-cat-all" onClick={() => setActiveCategory("all")}
          className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${activeCategory === "all" ? "bg-gray-800 text-white border-gray-800" : "bg-white text-gray-600 hover:bg-gray-50"}`}>
          TÜMÜ <span className="opacity-70">({rows.length})</span>
        </button>
        {categories.map(cat => {
          const active = activeCategory === cat.id;
          return (
            <button key={cat.id} data-testid={`lg-cat-${cat.slug}`} onClick={() => setActiveCategory(cat.id)}
              className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${active ? "text-white shadow" : "bg-white text-gray-600 hover:bg-gray-50"}`}
              style={active ? { backgroundColor: cat.color, borderColor: cat.color } : { borderColor: "#e5e7eb" }}>
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: active ? "#fff" : cat.color }} />
              {cat.name} <span className="opacity-70">({catCounts.map[cat.id] || 0})</span>
            </button>
          );
        })}
        {catCounts.unmapped > 0 && (
          <button data-testid="lg-cat-unmapped" onClick={() => setActiveCategory("unmapped")}
            className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${activeCategory === "unmapped" ? "bg-amber-500 text-white border-amber-500" : "bg-white text-amber-600 border-amber-300 hover:bg-amber-50"}`}>
            ⚠ Eşlenmemiş <span className="opacity-70">({catCounts.unmapped})</span>
          </button>
        )}
      </div>

      {/* Eşlenmemiş: ürün-kategori eşleme paneli */}
      {activeCategory === "unmapped" && unmappedProducts.length > 0 && (
        <div className="px-4 py-2 border-b bg-amber-50/60 max-h-36 overflow-y-auto" data-testid="lg-unmapped-panel">
          <div className="text-[11px] font-semibold text-amber-700 mb-1.5">Eşlenmemiş ürünleri bir kategoriye bağlayın (tüm günlere uygulanır):</div>
          <div className="space-y-1">
            {unmappedProducts.map(p => (
              <div key={p.product_name} className="flex items-center gap-2 text-xs">
                <span className="flex-1 truncate text-gray-700" title={p.product_name}>{p.product_name || "(isimsiz)"} <span className="text-gray-400">· {p.count}</span></span>
                <select data-testid="lg-map-select" value={mapChoice[p.product_name] || ""}
                  onChange={(e) => setMapChoice(m => ({ ...m, [p.product_name]: e.target.value }))}
                  className="px-2 py-1 border rounded-md bg-white">
                  <option value="">Kategori seç…</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <button data-testid="lg-map-btn" onClick={() => mapProduct(p.product_name, mapChoice[p.product_name])}
                  className="px-2.5 py-1 rounded-md bg-amber-500 text-white font-semibold hover:bg-amber-600">Eşle</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* B2.1: boat tabs */}
      <div className="bg-white border-b px-3 py-2 flex items-center gap-2 overflow-x-auto" data-testid="boat-tabs">
        {tabs.map(t => {
          const active = String(boatFilter) === String(t.id);
          const isDrop = dragRowId != null && t.id !== "all";
          return (
            <button key={t.id} data-testid={`boat-tab-${t.id}`} onClick={() => setBoatFilter(t.id)}
              onDragOver={isDrop ? (e) => e.preventDefault() : undefined}
              onDrop={isDrop ? (e) => { e.preventDefault(); assignBoat(dragRowId, t.id); setDragRowId(null); } : undefined}
              className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors
                ${isDrop ? "ring-2 ring-blue-300 ring-offset-1" : ""}
                ${active ? "bg-blue-600 text-white" : `bg-gray-100 text-gray-700 hover:bg-gray-200 ${tabColor(t.pax, t.cap)}`}`}>
              <span>{t.label}</span>
              <span className={`text-[11px] font-bold ${active ? "text-blue-100" : "text-gray-500"}`}>
                {t.cap && t.cap > 0 ? `${t.pax}/${t.cap}` : t.pax}
              </span>
            </button>
          );
        })}
        <div className="flex-1" />
        {boatFilter !== "all" && (
          <a data-testid="export-boat-btn" href={`${API}/api/export/sheet/${dateStr}?boat_id=${boatFilter}&token=${encodeURIComponent(token || "")}`}
            className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100">
            <Download className="w-3.5 h-3.5" /> Tekne listesi
          </a>
        )}
      </div>

      {/* B2.3: time chips (only when >1 distinct time) */}
      {timeChips.length > 1 && (
        <div className="bg-gray-50 border-b px-3 py-1.5 flex items-center gap-1.5 overflow-x-auto" data-testid="time-chips">
          <span className="text-[10px] font-semibold text-gray-400 uppercase shrink-0 mr-1">Saat</span>
          {timeChips.map(({ t, pax }) => {
            const active = timeFilters.has(t);
            return (
              <button key={t} data-testid={`time-chip-${t.replace(":", "-")}`}
                onClick={() => setTimeFilters(prev => { const n = new Set(prev); n.has(t) ? n.delete(t) : n.add(t); return n; })}
                className={`shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors
                  ${active ? "bg-indigo-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"}`}>
                {t === "saatsiz" ? "saatsiz" : t} <span className={active ? "text-indigo-100" : "text-gray-400"}>· {pax}</span>
              </button>
            );
          })}
          {timeFilters.size > 0 && (
            <button data-testid="time-clear" onClick={() => setTimeFilters(new Set())} className="shrink-0 text-[11px] text-gray-400 hover:text-gray-600 ml-1">temizle</button>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Yukleniyor...</div>
      ) : (
        <div className="flex-1 overflow-hidden p-3">
          <div className="bg-white border rounded-lg shadow-sm overflow-hidden h-full">
            <div ref={scrollRef} style={{ height: "100%", overflow: "auto", position: "relative" }} data-testid="grid-scroll">
              <div style={{ width: GRID_WIDTH, minHeight: totalHeight + HEADER_HEIGHT, position: "relative" }}>
                {/* Header */}
                <div style={{ position: "sticky", top: 0, zIndex: 25, height: HEADER_HEIGHT, display: "flex", backgroundColor: "#F8FAFC", borderBottom: "2px solid #E2E8F0" }}>
                  <div style={{ position: "sticky", left: 0, zIndex: 30, display: "flex", backgroundColor: "#F8FAFC" }}>
                    <div style={{ width: CHECKBOX_W, minWidth: CHECKBOX_W, height: HEADER_HEIGHT }} className="flex items-center justify-center border-r border-gray-200 shrink-0">
                      <input type="checkbox" data-testid="select-all-visible" checked={allVisibleSelected} onChange={toggleSelectAll} className="w-3.5 h-3.5 cursor-pointer" />
                    </div>
                    {COLUMNS.slice(0, FROZEN_COUNT).map(col => (
                      <div key={col.key} style={{ width: col.width, minWidth: col.width, height: HEADER_HEIGHT }}
                        className="flex items-center px-2 text-[11px] font-bold text-gray-600 uppercase tracking-wide border-r border-gray-200 shrink-0">{col.label}</div>
                    ))}
                  </div>
                  {COLUMNS.slice(FROZEN_COUNT).map(col => (
                    <div key={col.key} style={{ width: col.width, minWidth: col.width, height: HEADER_HEIGHT }}
                      className={`flex items-center px-2 text-[11px] font-bold uppercase tracking-wide border-r border-gray-200 shrink-0 ${col.editable ? "text-blue-600" : "text-gray-600"}`}>{col.label}</div>
                  ))}
                </div>

                {/* Rows */}
                <div style={{ position: "relative", height: totalHeight }}>
                  {virtualRows.map(vRow => {
                    const row = visibleRows[vRow.index];
                    if (!row) return null;
                    const checked = selectedRowIds.has(row.id);
                    return (
                      <div key={vRow.key}
                        draggable={editingCell == null}
                        onDragStart={(e) => { setDragRowId(row.id); e.dataTransfer.effectAllowed = "move"; try { e.dataTransfer.setData("text/plain", String(row.id)); } catch {} }}
                        onDragEnd={() => setDragRowId(null)}
                        style={{ position: "absolute", top: vRow.start, left: 0, height: ROW_HEIGHT, display: "flex", width: GRID_WIDTH }}
                        className={`border-b border-gray-100 ${checked ? "bg-blue-50/40" : ""} ${dragRowId === row.id ? "opacity-50" : ""}`}>
                        {/* Frozen */}
                        <div style={{ position: "sticky", left: 0, zIndex: 10, display: "flex", backgroundColor: checked ? "#EFF6FF" : "white" }}>
                          <div style={{ width: CHECKBOX_W, minWidth: CHECKBOX_W, height: ROW_HEIGHT }} className="flex items-center justify-center border-r border-gray-100 shrink-0">
                            <input type="checkbox" data-testid={`row-check-${vRow.index}`} checked={checked} onChange={() => toggleRow(row.id)} className="w-3.5 h-3.5 cursor-pointer" />
                          </div>
                          {COLUMNS.slice(0, FROZEN_COUNT).map((col, ci) => (
                            <Cell key={col.key} row={row} col={col} colIdx={ci} ri={vRow.index} boats={boats} boatMap={boatMap}
                              isSelected={selectedCell?.rowIdx === vRow.index && selectedCell?.colIdx === ci}
                              isEditing={editingCell?.rowIdx === vRow.index && editingCell?.colIdx === ci}
                              isConflict={conflict?.rowId === row.id && conflict?.colIdx === ci}
                              onSelect={() => setSelectedCell({ rowIdx: vRow.index, colIdx: ci })}
                              onStartEdit={() => startEdit(vRow.index, ci)}
                              onCommit={(v) => commitEdit(vRow.index, ci, v)}
                              onCancelEdit={() => setEditingCell(null)} />
                          ))}
                        </div>
                        {/* Scrollable */}
                        {COLUMNS.slice(FROZEN_COUNT).map((col, i) => {
                          const ci = FROZEN_COUNT + i;
                          return (
                            <Cell key={col.key} row={row} col={col} colIdx={ci} ri={vRow.index} boats={boats} boatMap={boatMap} customTags={customTags}
                              isSelected={selectedCell?.rowIdx === vRow.index && selectedCell?.colIdx === ci}
                              isEditing={editingCell?.rowIdx === vRow.index && editingCell?.colIdx === ci}
                              isConflict={conflict?.rowId === row.id && conflict?.colIdx === ci}
                              onSelect={() => setSelectedCell({ rowIdx: vRow.index, colIdx: ci })}
                              onStartEdit={() => startEdit(vRow.index, ci)}
                              onCommit={(v) => commitEdit(vRow.index, ci, v)}
                              onCancelEdit={() => setEditingCell(null)} />
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* B2.2: bulk-move action bar */}
      {selectedCount > 0 && (
        <div className="border-t bg-blue-600 text-white px-4 py-2 flex items-center gap-3 text-xs" data-testid="bulk-bar">
          <span className="font-semibold" data-testid="bulk-selected-info">{selectedCount} rezervasyon · {selectedPax} kisi secildi</span>
          <div className="flex-1" />
          <MoveRight className="w-4 h-4" />
          <select data-testid="bulk-move-select" defaultValue="" onChange={(e) => { if (e.target.value) { doBulkMove(e.target.value); e.target.value = ""; } }}
            className="text-gray-800 rounded px-2 py-1 text-xs font-medium outline-none">
            <option value="" disabled>Secilenleri tasi →</option>
            {boats.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <button data-testid="bulk-clear" onClick={() => setSelectedRowIds(new Set())} className="px-2 py-1 rounded bg-blue-500 hover:bg-blue-400 font-medium">Temizle</button>
        </div>
      )}

      {/* Bottom status bar */}
      <div className="border-t bg-white px-4 py-2 flex items-center justify-between gap-3 text-xs" data-testid="grid-status-bar">
        {conflict ? (
          <div className="flex items-center gap-3 flex-1" data-testid="conflict-bar">
            <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
            <span className="text-red-600 font-medium">
              Bu hucreyi <b>{conflict.updated_by || "baska kullanici"}</b> degistirdi (v{conflict.current_version}).
              Sunucudaki deger: <b>"{String(conflict.current_value ?? "")}"</b>
            </span>
            <button onClick={resolveRefresh} data-testid="conflict-refresh" className="flex items-center gap-1 px-2.5 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium">
              <RefreshCw className="w-3 h-3" /> Yenile
            </button>
            <button onClick={resolveOverwrite} data-testid="conflict-overwrite" className="flex items-center gap-1 px-2.5 py-1 rounded bg-red-500 hover:bg-red-600 text-white font-medium">
              <X className="w-3 h-3" /> Uzerine yaz
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {status.type === "saving" && <span className="text-blue-600 flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" /> {status.msg}</span>}
            {status.type === "saved" && <span className="text-green-600 flex items-center gap-1"><Check className="w-3 h-3" /> {status.msg}</span>}
            {status.type === "error" && <span className="text-red-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {status.msg}</span>}
            {status.type === "idle" && <span className="text-gray-400">Cift tikla veya Enter ile duzenle · Checkbox ile secip toplu tasi</span>}
          </div>
        )}
        <div className="flex items-center gap-3 shrink-0">
          {lastPaste && lastPaste.length > 0 && (
            <button data-testid="undo-paste" onClick={undoPaste}
              className="text-xs font-medium px-2.5 py-1 rounded bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-300">
              ↩ Son yapistirmayi geri al
            </button>
          )}
          <span className="text-gray-400">
            {selectedCell ? `Secili: ${COLUMNS[selectedCell.colIdx].label}` : "Hucre secili degil"}
          </span>
        </div>
      </div>

      <ManualEntryDialog open={manualOpen} onClose={() => setManualOpen(false)} defaultDate={dateStr}
        onConfirmed={() => fetchSheetSilent()} />

      {/* D4: blok yapistirma onizleme */}
      {pasteData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="paste-preview-dialog">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <h2 className="text-base font-bold text-gray-800">Blok Yapistirma Onizleme</h2>
              <button data-testid="paste-cancel" onClick={() => setPasteData(null)} className="p-1.5 rounded hover:bg-gray-100 text-gray-500"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-5 py-2 border-b bg-gray-50 text-sm text-gray-700" data-testid="paste-summary">
              <b>{pasteData.changes.length}</b> hucre guncellenecek.
              {pasteData.warnings.map((w, i) => (
                <div key={i} className="text-[11px] text-amber-700 flex items-center gap-1 mt-1"><AlertTriangle className="w-3 h-3" /> {w}</div>
              ))}
            </div>
            <div className="flex-1 overflow-auto p-3">
              <table className="w-full text-xs">
                <thead className="text-gray-400 uppercase text-[10px]">
                  <tr><th className="text-left px-2 py-1">Musteri</th><th className="text-left px-2 py-1">Sutun</th><th className="text-left px-2 py-1">Eski</th><th className="text-left px-2 py-1">Yeni</th></tr>
                </thead>
                <tbody>
                  {pasteData.changes.map((ch, i) => (
                    <tr key={i} data-testid={`paste-change-${i}`} className="border-t border-gray-100">
                      <td className="px-2 py-1 truncate max-w-[140px]">{ch.customer || "-"}</td>
                      <td className="px-2 py-1 font-semibold text-blue-600">{ch.colLabel}</td>
                      <td className="px-2 py-1 text-gray-400 line-through">{ch.oldValue || "—"}</td>
                      <td className="px-2 py-1 font-medium text-gray-800">{ch.newValue || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-end gap-2">
              <button data-testid="paste-cancel-2" onClick={() => setPasteData(null)} className="text-sm px-3 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium">Vazgec</button>
              <button data-testid="paste-apply" onClick={applyPaste} disabled={!pasteData.changes.length}
                className="text-sm px-4 py-1.5 rounded bg-green-600 hover:bg-green-700 text-white font-medium flex items-center gap-1.5 disabled:opacity-50">
                <Check className="w-4 h-4" /> Onayla ve uygula
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
